import React, { useState, useRef, useEffect, useCallback, useMemo, ChangeEvent } from 'react'
import { Box, Icon } from '../index'
import { DropdownItem } from './DropdownItem'
import { DropdownContent, type DropdownContentProps } from './DropdownContent'
import type { DropdownItemProps as DropdownItemPropsBase } from './DropdownItem'

export type DropdownItemProps<Dropdown> = DropdownItemPropsBase & {
  dropdown?: Dropdown
}

export type DropdownProps = Omit<DropdownContentProps, 'target'> & {
  contentClass?: any,
  items?: DropdownItemProps<DropdownProps>[],
  prepend?: React.ReactNode,
  append?: React.FC,
  activeMode?: 'click' | 'hover',
  onClick?: (e: Event) => void,
  onClickItem?: (item: DropdownItemPropsBase, e: Event) => void
  onActiveChange?: (value: boolean) => void
  scrollable?: boolean,
  filterable?: boolean,
  countText?: string,
  searchPlaceholder?: string
}

export const Dropdown = React.forwardRef(function Dropdown ({
  className,
  style,
  contentClass,
  children,
  prepend,
  append,
  items = [],
  position = 'bottom',
  align = 'start',
  offsetX = 0,
  offsetY = 0,
  activeMode = 'click',
  onClick,
  onClickItem,
  // onFocus,
  // onBlur,
  theme,
  bgColor = 'surface',
  color = 'base',
  onActiveChange,
  open = false,
  scrollable = false,
  filterable = false,
  countText = '',
  searchPlaceholder = '',
  ...props
} : DropdownProps, ref) {

  const rootRef = useRef<HTMLSpanElement | null>(null)
  const getRootRef = (node: HTMLSpanElement | null) => {
    try {
      rootRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    } catch {}
  }

  const [active, setActive] = useState<boolean>(open)
  
  useEffect(() => {
    onActiveChange && onActiveChange(active)
  },[active])
  
  const cleanItem = (item: DropdownItemProps<DropdownProps>) => {
    item = { ...item }
    delete item.dropdown
    return item
  }

  useEffect(() => {
    setActive(open)
  }, [open])

  // Hover mode

  const [hoverInterval, setHoverInterval] = useState<NodeJS.Timeout>()

  const onMouseover = useCallback(() => {
    clearInterval(hoverInterval)
    const activeElement = document.activeElement as HTMLElement
    activeElement?.blur() // Close dropdown
    setActive(true)
  }, [setActive, hoverInterval])

  const onMouseleave = useCallback(() => {
    clearInterval(hoverInterval)
    setHoverInterval(
      setTimeout(() => {
        setActive(false)
      }, 300)
    )
  }, [setActive, setHoverInterval, hoverInterval])

  // Click mode

  const clickHandler = useCallback((e: Event) => {
    onClick && onClick(e)
    setActive(true)
  }, [onClick, setActive])

  const clickItemHandler = useCallback((item: DropdownItemProps<DropdownProps>, e: Event) => {
    e.stopPropagation()
    onClickItem && onClickItem(item, e)
  }, [onClickItem])

  // const onBlurHandler = useCallback((e: FocusEvent) => {
  //   if (rootRef.current?.contains(e.relatedTarget as Node) || !document.hasFocus()) return
  //   onBlur && onBlur(e)
  //   setActive(false)
  // }, [onBlur, rootRef, setActive])

  // Filterable

  let _searchInterval: number
  const [search, setSearch] = useState<string>('')

  const setLazySearch = (e: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(_searchInterval)
    _searchInterval = window.setTimeout(() => {
      setSearch(e.target.value)
    }, 300)
  }

  const filteredItems = useMemo<DropdownItemProps<DropdownProps>[]>(() => {
    if (!filterable || !search) return items
    const _s = search.toLowerCase()
    return items.filter(({ text }) => text != null && String(text).toLowerCase().includes(_s))
  }, [filterable, items, search])

  const showingText = useMemo(() => {
    return countText
      .replace('{count}', String(filteredItems.length))
      .replace('{total}', String(items.length))
  }, [
    items,
    countText,
    filteredItems
  ])

  // Content

  const contentWidth = useMemo<number | undefined>(() => {
    return Number(props.width || props.maxWidth || props.minWidth) || undefined
  }, [props.width, props.maxWidth, props.minWidth])

  const contentHeight = useMemo<number | undefined>(() => {
    return Number(props.height || props.maxHeight || props.minHeight) || undefined
  }, [props.width, props.maxWidth, props.minWidth])

  return (
    <Box      
      tag="span"
      ref={getRootRef}
      tabIndex="0"
      className={['topbar-dropwdown', activeMode, active && 'active', className]}
      style={style}
      onClick={clickHandler}
      onMouseOver={activeMode === 'hover' ? onMouseover : undefined}
      onMouseLeave={activeMode === 'hover' ? onMouseleave : undefined}
    >
      {children}
      <DropdownContent
        show={active}
        className={contentClass}
        position={position}
        align={align}
        offsetX={offsetX}
        offsetY={offsetY}
        target={rootRef.current ?? null}
        width={contentWidth}
        height={contentHeight}
        theme={theme}
        onClickOutside={() => setActive(false)}
      >
        <Box
          width={contentWidth ? '100%' : null}
          className={['topbar-dropwdown-content', { scrollable }]}
          bgColor={bgColor}
          color={color}
          theme={theme}
        >
          {(prepend || filterable) && (
            <div className="topbar-dropwdown-content__prepend">
              {prepend}
              {filterable && (
                <div className="pt-5 px-4">
                  <Box
                    className="flex outlined-1 rounded-sm mb-4"
                    borderColor="border"
                    bgColor="black-grey"
                    color="soft-grey"
                  >
                    <input
                      id="searchTerm"
                      name="searchTerm"
                      type="text"
                      className="flex-grow-1 text-inherit bg-transparent pl-4"
                      style={{ minWidth: 0 }}
                      placeholder={searchPlaceholder}
                      autoComplete="off"
                      onInput={setLazySearch}
                    />
                    <Icon
                      name="icon-search-outline"
                      className="flex-shrink-0 mx-4 my-2"
                      color="base"
                      size="xl"
                    />
                  </Box>
                  {showingText && (
                    <p className="text-xs text-base text-lighten-2 mb-0">
                      {showingText}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="topbar-dropwdown-content__body">
            {filterable && filteredItems.length === 0 ? (
              <Box
                height="57"
                bgColor="dark-grey"
                className="flex align-center justify-center rounded-sm m-4"
              >
                Sin resultados
              </Box>
            ) : (
              filteredItems.map((itemProps, index) => (
                itemProps.dropdown
                  ? (
                    <Dropdown
                      activeMode={activeMode}
                      onClickItem={onClickItem}
                      bgColor={bgColor}
                      color={color}
                      {...props}
                      {...itemProps.dropdown}
                      theme={theme}
                      key={itemProps.id || index}
                    >
                      <DropdownItem
                        {...cleanItem(itemProps)}
                        appendIcon="icon-chevron-right"
                      />
                    </Dropdown>
                  ) : (
                    <DropdownItem
                      {...itemProps}
                      key={itemProps.id || index}
                      onClick={(e: Event) => clickItemHandler(itemProps, e)}
                    />
                  )
              ))
            )}
          </div>
          {append}
        </Box>
      </DropdownContent>
    </Box>
  )
}) 